import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, createTextVNode as _createTextVNode, renderSlot as _renderSlot, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "inner" }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "lastUpdatedWrapper" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { key: 1 }

import { formatInTimeZone } from 'date-fns-tz';
import { enGB } from 'date-fns/locale';
import {
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { INFO_CARD_COLOR } from '@constants/info-card-color';
import { isFuture, isPast } from 'date-fns';
import { timezone, routeInformationUrl } from '../../envVariables';
import {
  RouteDetail,
  Icon,
  mapRoutes,
  mapStatusToRteContent,
  parseRouteStatus,
  sortRouteSailingStatusByStartDate,
  sortRouteSailingStatusByPriority,
  sortRouteServiceStatusTypeByPriority,
  RouteServiceStatus,
  RteContentType,
  ServiceStatusType,
  InfoCard,
} from '../../index';
import { useServiceStatusStore } from '../../stores';
import {
  Route, RouteStatus, StatusCategory, SubStatus,
} from '../../types/api';
import { ServiceStatusTabs } from '../ServiceStatusTabs';

const POLLING_INTERVAL_MS = 60000; // 1 min polling

export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceStatusItem',
  props: {
    routeCode: {},
    isMobileAppView: { type: Boolean },
    routeToTimetable: { type: Function },
    initialTab: {}
  },
  emits: ['tabChange'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const onTabChange = (tab: string) => {
  emit('tabChange', tab);
};

const { t } = useI18n();
const serviceStatusUrl = `${window.location.origin}${window.location.pathname}#/service-status`;
let stopRouteStatusUpdatesFetch: (() => void) | undefined;
let stopServiceStatusUpdatesFetch: (() => void) | undefined;

const serviceStatusStore = useServiceStatusStore();

const route = ref<RouteServiceStatus>();
const statusUpdated = ref<string>();
const tabsLoading = ref<boolean>(false);
const sailingStatusReports = ref<
  Array<{
    status: ServiceStatusType;
    statusHeading: string;
    rteContent?: Array<RteContentType>;
    showAlternativeCta?: boolean;
    showRefundCta?: boolean;
    startDate?: string;
  }>
>([]);
const serviceStatuses = ref<Array<RteContentType>>();
const informationStatuses = ref<Array<RteContentType>>();

const redirectToRouteInfo = () => {
  window.open(`${routeInformationUrl}/${route.value?.routeCode}`);
};

const getStatuses = (statusItem: Route, category: StatusCategory) => statusItem
  .routeStatuses.filter(
    (routeStatus: RouteStatus) => routeStatus.status === category,
  );

const getStatusHeadingBySubStatus = (subStatus: SubStatus) => {
  switch (subStatus) {
    case SubStatus.AllSailingsCancelled:
      return t('Cancelled');
    case SubStatus.BeAware:
      return t('Be aware / At risk');
    case SubStatus.Disruptions:
      return t('One or more sailings are disrupted');
    default:
      return t('Normal service');
  }
};

const renderServiceStatusItem = (serviceStatusItem: Route) => {
  const routeSailingStatuses = serviceStatusItem?.routeStatuses.filter(
    (routeStatus: RouteStatus) => routeStatus.status === StatusCategory.Sailing,
  );

  let showNormalServiceStatus = true;

  if (routeSailingStatuses.length > 0) {
    const routeSailingStatus = routeSailingStatuses.sort(
      sortRouteSailingStatusByPriority,
    );

    if (routeSailingStatus[0]?.updatedAtDateTime) {
      statusUpdated.value = formatInTimeZone(
        Date.parse(routeSailingStatus[0]?.updatedAtDateTime),
        timezone,
        'dd MMM yyyy',
        { locale: enGB },
      );
    }

    sailingStatusReports.value = routeSailingStatus
      .map((item) => {
        const content = mapStatusToRteContent(item);

        const showCta = !props.isMobileAppView && (item.subStatus === SubStatus.AllSailingsCancelled
            || item.subStatus === SubStatus.Disruptions);

        const activeStatusReport = isPast(new Date(item.startDateTime))
          && isFuture(new Date(item.endDateTime));

        if (activeStatusReport) {
          showNormalServiceStatus = false;
        }

        return {
          status: parseRouteStatus(item.subStatus),
          statusHeading: getStatusHeadingBySubStatus(item.subStatus),
          rteContent: [content],
          showAlternativeCta: showCta && route.value && route.value.ports.length <= 2,
          showRefundCta: showCta,
          startDate: item.startDateTime
            ? formatInTimeZone(item.startDateTime, timezone, "yyyy-MM-dd'T'HH:mm")
            : undefined,
        };
      })
      .sort(sortRouteServiceStatusTypeByPriority);
  } else {
    sailingStatusReports.value = [];
  }

  const normalReport = {
    status: ServiceStatusType.Normal,
    statusHeading: t('Normal service'),
  };

  if (sailingStatusReports.value.length > 0) {
    const sortedStatusReports = [...sailingStatusReports.value].sort(
      sortRouteSailingStatusByStartDate,
    );

    sailingStatusReports.value = showNormalServiceStatus
      ? [normalReport, ...sortedStatusReports]
      : sortedStatusReports;
  } else {
    sailingStatusReports.value = [normalReport];
  }

  const routeServiceStatuses = getStatuses(
    serviceStatusItem,
    StatusCategory.Service,
  );

  if (routeServiceStatuses.length > 0) {
    serviceStatuses.value = routeServiceStatuses.map((item) => mapStatusToRteContent(item));
  } else {
    serviceStatuses.value = [];
  }

  const routeInformationStatuses = getStatuses(
    serviceStatusItem,
    StatusCategory.Information,
  );

  if (routeInformationStatuses.length > 0) {
    informationStatuses.value = routeInformationStatuses.map((item) => mapStatusToRteContent(item));
  } else {
    informationStatuses.value = [];
  }
};

const populateView = async (selectedRoute: Route) => {
  route.value = mapRoutes(selectedRoute);

  if (stopRouteStatusUpdatesFetch) { // Stop receiving updates for the old route
    stopRouteStatusUpdatesFetch();
  }
  stopRouteStatusUpdatesFetch = await serviceStatusStore.getRouteServiceStatus(
    props.routeCode,
    POLLING_INTERVAL_MS,
  );
};

onMounted(async () => {
  // Setup
  stopServiceStatusUpdatesFetch = await serviceStatusStore.getServiceStatus(POLLING_INTERVAL_MS);
});

watch(
  () => serviceStatusStore.serviceStatus,
  (newStatuses, oldStatuses) => {
    if (!newStatuses || newStatuses.length === 0) {
      return;
    }

    const selectedRoute = newStatuses.find(
      (routeWithStatus) => routeWithStatus.routeCode === props.routeCode,
    );

    if (!selectedRoute) {
      return;
    }

    const oldRoute = (oldStatuses || []).find(
      (routeWithStatus) => routeWithStatus.routeCode === props.routeCode,
    );
    // Only update the route information if the route has changed or service status has changed
    // actually changed for this route
    if (!oldRoute
      || selectedRoute.status !== oldRoute.status
      || selectedRoute.id !== oldRoute.id) {
      populateView(selectedRoute);
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => serviceStatusStore.isServiceStatusLoading,
  (newVal) => {
    tabsLoading.value = newVal;
  },
  {
    immediate: true,
  },
);

watch(
  () => serviceStatusStore.routeServiceStatus,
  (newRoute) => {
    if (newRoute) {
      renderServiceStatusItem(newRoute);
  }
},
  {
    immediate: true,
  },
);

// If routecode changes then update the view and perform further calls
// to fetch route service status + updates
watch(
  () => props.routeCode,
  async (newRouteCode, oldRouteCode) => {
    if (newRouteCode !== oldRouteCode) {
      const selectedRoute = (serviceStatusStore.serviceStatus || []).find(
        (item) => item.routeCode === props.routeCode,
      );
      if (selectedRoute) {
        await serviceStatusStore.getServiceStatus();
        populateView(selectedRoute);
      }
    }
  },
  {
    immediate: true,
  },
);

onUnmounted(() => {
  if (stopRouteStatusUpdatesFetch) {
    stopRouteStatusUpdatesFetch();
  }
  if (stopServiceStatusUpdatesFetch) {
    stopServiceStatusUpdatesFetch();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (!_ctx.isMobileAppView)
          ? (_openBlock(), _createElementBlock("a", {
              key: 0,
              class: "backButton",
              href: serviceStatusUrl,
              "aria-label": "service status link"
            }, [
              _createVNode(_unref(Icon), {
                "icon-name": "redLink",
                class: "close-icon"
              }),
              _cache[0] || (_cache[0] = _createElementVNode("span", null, "Back", -1))
            ]))
          : _createCommentVNode("", true),
        (!_ctx.isMobileAppView)
          ? (_openBlock(), _createElementBlock("h2", _hoisted_3, _toDisplayString(_ctx.$t("Route service status")), 1))
          : _createCommentVNode("", true),
        (_unref(serviceStatusStore).isServiceStatusError)
          ? (_openBlock(), _createBlock(_unref(InfoCard), {
              key: 2,
              "icon-name": "warning",
              text: _ctx.$t('Trouble retrieving service'),
              lg: true,
              "background-color": _unref(INFO_CARD_COLOR).white,
              "border-color": _unref(INFO_CARD_COLOR).red
            }, null, 8, ["text", "background-color", "border-color"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(RouteDetail), {
          status: route.value?.status,
          ports: route.value?.ports,
          "destination-name": route.value?.destinationName
        }, {
          lastUpdated: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              (statusUpdated.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_ctx.$t("Last updated text and time", [statusUpdated.value])), 1))
                : (_openBlock(), _createElementBlock("p", _hoisted_6, "   ")),
              (route.value?.ports?.length < 3 && !route.value?.isFreight && !route.value?.hideOnMap)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 2,
                    onClick: () => _ctx.routeToTimetable(route.value),
                    class: "link"
                  }, [
                    _createTextVNode(_toDisplayString(_ctx.$t("View timetable")) + " ", 1),
                    _createVNode(_unref(Icon), { "icon-name": "redLink" })
                  ], 8, _hoisted_7))
                : _createCommentVNode("", true)
            ])
          ]),
          favourite: _withCtx(() => [
            _renderSlot(_ctx.$slots, "favourite")
          ]),
          infoCard: _withCtx(() => [
            _renderSlot(_ctx.$slots, "infoCard")
          ]),
          _: 3
        }, 8, ["status", "ports", "destination-name"])
      ])
    ]),
    (!tabsLoading.value)
      ? (_openBlock(), _createBlock(_unref(ServiceStatusTabs), {
          key: 0,
          "sailing-status-reports": sailingStatusReports.value,
          "service-statuses": serviceStatuses.value,
          "information-statuses": informationStatuses.value,
          "route-statuses": _unref(serviceStatusStore).routeServiceStatus?.routeStatuses,
          "on-find-alternate-click": () => redirectToRouteInfo(),
          "initial-tab": props.initialTab,
          onTabChange: onTabChange
        }, {
          liveUpdatesInfoCard: _withCtx(() => [
            _renderSlot(_ctx.$slots, "liveUpdatesInfoCard")
          ]),
          _: 3
        }, 8, ["sailing-status-reports", "service-statuses", "information-statuses", "route-statuses", "on-find-alternate-click", "initial-tab"]))
      : _createCommentVNode("", true),
    (tabsLoading.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Loading... "))
      : _createCommentVNode("", true)
  ], 64))
}
}

})